import React, { useState, useEffect } from 'react';
import './SidebarNav.css';
import Popup from './Popup';

const SidebarNav = ({ userInfo, emotionCount }) => {
  const [isOpen, setIsOpen] = useState(false);

  // State for PIN change popup and fields
  const [showPinPopup, setShowPinPopup] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');

  // Add new state for loading
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Close nav on Escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // Don't close the nav if the PIN popup is open
        if (showPinPopup) return;
        if (isOpen) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, showPinPopup]);

  const handlePinChangeSubmit = async () => {
    // Basic validations
    if (newPin.trim() !== confirmPin.trim()) {
      setPinError('Pins do not match.');
      return;
    }
    if (newPin.length !== 4) {
      setPinError('PIN must be 4 digits.');
      return;
    }
    setPinError('');
    setPinSuccess('');
    setIsSubmitting(true); // Set loading state

    try {
      const response = await fetch('/api/auth?action=change-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userInfo.email,
          newPin
        })
      });
      if (response.ok) {
        setPinSuccess('PIN changed successfully.');
        setTimeout(() => {
          setShowPinPopup(false);
          setNewPin('');
          setConfirmPin('');
          setPinSuccess('');
        }, 1500);
      } else {
        setPinError('Failed to change PIN. Please try again.');
      }
    } catch (error) {
      console.error('Error changing PIN:', error);
      setPinError('Error changing PIN. Please try again.');
    } finally {
      setIsSubmitting(false); // Reset loading state
    }
  };

  return (
    <>
      <button 
        className={`floating-button nav ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle navigation"
      >
        ☰
      </button>
      
      {isOpen && (
        <div className="nav-overlay" onClick={() => setIsOpen(false)}>
          <div className="nav-panel" onClick={(e) => e.stopPropagation()}>
            <div className="nav-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="user-email">{userInfo.email}</span>
              <button 
                type="button" 
                className="close-button"
                onClick={() => setIsOpen(false)}
                aria-label="Close navigation"
              >
                ×
              </button>
            </div>
            
            <div className="nav-content">
              <div className="emotion-stats">
                <span className="stats-label">Feelings discovered:</span>
                {emotionCount} / 220
              </div>
              
              <ul className="nav-menu">
                <li>
                  <button onClick={() => setShowPinPopup(true)} className="nav-link">
                    Change PIN
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showPinPopup && (
        <Popup onClose={() => setShowPinPopup(false)}>
          <h3>Change PIN</h3>
          <input 
            type="password" 
            placeholder="Enter new 4-digit PIN" 
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.slice(0, 4))}
            autoFocus
          />
          <input 
            type="password" 
            placeholder="Confirm your new PIN" 
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.slice(0, 4))}
          />
          {pinError && <p className="error-message">{pinError}</p>}
          {pinSuccess && <p className="success-message">{pinSuccess}</p>}
          <div className="popup-buttons">
            <button 
              onClick={handlePinChangeSubmit} 
              disabled={isSubmitting}
              className={isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button 
              onClick={() => setShowPinPopup(false)} 
              disabled={isSubmitting}
              className={isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
            >
              Cancel
            </button>
          </div>
        </Popup>
      )}
    </>
  );
};

export default SidebarNav;